"use client"

import { useState, useEffect } from "react"
import "./MissionControl.css"

const MissionControl = ({ gameState, onUpdateGameState, addNotification }) => {
  const [activeMissions, setActiveMissions] = useState([])
  const [availableMissions, setAvailableMissions] = useState([
    {
      id: "asteroid_mining",
      name: "Asteroid Mining Expedition",
      description: "Send a mining team to nearby asteroids for rare materials",
      duration: 120,
      requirements: { engineering: 20, materials: 100, energy: 150 },
      rewards: { materials: 300, knowledge: 50 },
      riskLevel: "Medium",
    },
    {
      id: "deep_space_scan",
      name: "Deep Space Scanning",
      description: "Deploy long-range sensors to discover new planets",
      duration: 180,
      requirements: { intelligence: 30, energy: 200, knowledge: 100 },
      rewards: { knowledge: 200, evolutionPoints: 25 },
      riskLevel: "Low",
    },
    {
      id: "rescue_mission",
      name: "Emergency Rescue Mission",
      description: "Rescue stranded colonists from a failed expedition",
      duration: 90,
      requirements: { engineering: 25, energy: 100, materials: 50 },
      rewards: { population: 100, morale: 15 },
      riskLevel: "High",
    },
  ])

  const [missionLog, setMissionLog] = useState([
    {
      id: 1,
      name: "Orbital Survey",
      completed: Date.now() - 3600000,
      success: true,
      rewards: { knowledge: 75, evolutionPoints: 15 },
    },
    {
      id: 2,
      name: "Resource Extraction",
      completed: Date.now() - 7200000,
      success: true,
      rewards: { materials: 150, energy: 100 },
    },
  ])

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMissions((prev) =>
        prev
          .map((mission) => {
            const timeLeft = mission.endTime - Date.now()
            if (timeLeft <= 0) {
              completeMission(mission)
              return null
            }
            return { ...mission, timeLeft }
          })
          .filter(Boolean),
      )
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const canStartMission = (mission) => {
    return Object.entries(mission.requirements).every(([requirement, amount]) => {
      if (gameState.resources[requirement] !== undefined) {
        return gameState.resources[requirement] >= amount
      }
      if (gameState.user[requirement] !== undefined) {
        return gameState.user[requirement] >= amount
      }
      return false
    })
  }

  const startMission = (mission) => {
    if (!canStartMission(mission)) {
      addNotification("Mission requirements not met", "error")
      return
    }

    // Deduct resources
    const newResources = { ...gameState.resources }
    Object.entries(mission.requirements).forEach(([requirement, amount]) => {
      if (newResources[requirement] !== undefined) {
        newResources[requirement] -= amount
      }
    })

    onUpdateGameState({ resources: newResources })

    // Start mission
    const activeMission = {
      ...mission,
      startTime: Date.now(),
      endTime: Date.now() + mission.duration * 1000,
      timeLeft: mission.duration * 1000,
    }

    setActiveMissions((prev) => [...prev, activeMission])
    addNotification(`Mission "${mission.name}" launched!`, "success")
  }

  const completeMission = (mission) => {
    const successRates = { Low: 0.7, Medium: 0.6, High: 0.5 }
    const baseSuccessRate = successRates[mission.riskLevel] || 0.6
    const levelBonus = gameState.user.level * 0.02
    const finalSuccessRate = Math.min(0.95, baseSuccessRate + levelBonus)

    const isSuccess = Math.random() < finalSuccessRate

    if (isSuccess) {
      // Award rewards
      const newResources = { ...gameState.resources }
      const newUser = { ...gameState.user }

      Object.entries(mission.rewards).forEach(([resource, amount]) => {
        if (resource === "evolutionPoints") {
          newUser.evolutionPoints = (newUser.evolutionPoints || 0) + amount
        } else if (resource === "morale") {
          gameState.colonyMorale = Math.min(100, gameState.colonyMorale + amount)
        } else {
          newResources[resource] = (newResources[resource] || 0) + amount
        }
      })

      onUpdateGameState({ resources: newResources, user: newUser })

      addNotification(
        `Mission "${mission.name}" completed successfully! Rewards: ${Object.entries(mission.rewards)
          .map(([k, v]) => `${v} ${k}`)
          .join(", ")}`,
        "success",
      )

      // Add to mission log
      setMissionLog((prev) => [
        {
          id: Date.now(),
          name: mission.name,
          completed: Date.now(),
          success: true,
          rewards: mission.rewards,
        },
        ...prev.slice(0, 9),
      ])
    } else {
      addNotification(`Mission "${mission.name}" failed!`, "error")

      setMissionLog((prev) => [
        {
          id: Date.now(),
          name: mission.name,
          completed: Date.now(),
          success: false,
          rewards: {},
        },
        ...prev.slice(0, 9),
      ])
    }

    // Remove from active missions
    setActiveMissions((prev) => prev.filter((m) => m.id !== mission.id))
  }

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  const getMissionIcon = (type) => {
    const icons = {
      exploration: "🔭",
      resource: "⛏️",
      science: "🧪",
      economic: "💼",
      military: "⚔️",
    }
    return icons[type] || "🚀"
  }

  const getRiskColor = (risk) => {
    switch (risk) {
      case "Low":
        return "#4caf50"
      case "Medium":
        return "#ff9800"
      case "High":
        return "#f44336"
      default:
        return "#4caf50"
    }
  }

  return (
    <div className="mission-control">
      <div className="mission-header">
        <h2 className="section-title">
          <span className="title-icon">🚀</span>
          Mission Control
        </h2>
        <p className="section-subtitle">Deep space exploration missions coming soon</p>
      </div>

      <div className="mission-stats">
        <div className="stat-card">
          <span className="stat-icon">🎯</span>
          <div className="stat-info">
            <span className="stat-value">{activeMissions.length}</span>
            <span className="stat-label">Active Missions</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">✅</span>
          <div className="stat-info">
            <span className="stat-value">{missionLog.filter((m) => m.success).length}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">📊</span>
          <div className="stat-info">
            <span className="stat-value">
              {missionLog.length > 0
                ? Math.round((missionLog.filter((m) => m.success).length / missionLog.length) * 100)
                : 0}
              %
            </span>
            <span className="stat-label">Success Rate</span>
          </div>
        </div>
      </div>

      <div className="mission-panels">
        {/* Active Missions */}
        <div className="mission-panel">
          <h3 className="panel-title">
            <span className="panel-icon">⏳</span>
            Active Missions ({activeMissions.length})
          </h3>
          <div className="missions-list">
            {activeMissions.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🌌</span>
                <p>No active missions</p>
                <p className="empty-subtitle">Launch a mission to begin exploration</p>
              </div>
            ) : (
              activeMissions.map((mission) => (
                <div key={mission.id} className="mission-card active">
                  <div className="mission-info">
                    <div className="mission-icon">{getMissionIcon(mission.type)}</div>
                    <div className="mission-details">
                      <h4 className="mission-name">{mission.name}</h4>
                      <p className="mission-description">{mission.description}</p>
                    </div>
                  </div>
                  <div className="mission-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${((mission.duration * 1000 - mission.timeLeft) / (mission.duration * 1000)) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="time-remaining">{Math.floor(mission.timeLeft / 1000)}s remaining</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Available Missions */}
        <div className="mission-panel">
          <h3 className="panel-title">
            <span className="panel-icon">📋</span>
            Available Missions
          </h3>
          <div className="missions-list">
            {availableMissions.map((mission) => (
              <div key={mission.id} className={`mission-card ${mission.unlocked ? "available" : "locked"}`}>
                <div className="mission-info">
                  <div className="mission-icon">{getMissionIcon(mission.type)}</div>
                  <div className="mission-details">
                    <div className="mission-header">
                      <h4 className="mission-name">{mission.name}</h4>
                      <span className="difficulty-badge" style={{ backgroundColor: getRiskColor(mission.riskLevel) }}>
                        {mission.riskLevel} Risk
                      </span>
                    </div>
                    <p className="mission-description">{mission.description}</p>
                    <div className="mission-duration">⏱️ Duration: {mission.duration}s</div>
                  </div>
                </div>

                {mission.unlocked ? (
                  <>
                    <div className="mission-requirements">
                      <h5>Requirements:</h5>
                      <div className="requirements-list">
                        {Object.entries(mission.requirements).map(([resource, amount]) => (
                          <span
                            key={resource}
                            className={`requirement ${(gameState.resources[resource] || gameState.user[resource] || 0) >= amount ? "met" : "unmet"}`}
                          >
                            {amount} {resource}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mission-rewards">
                      <h5>Rewards:</h5>
                      <div className="rewards-list">
                        {Object.entries(mission.rewards).map(([resource, amount]) => (
                          <span key={resource} className="reward">
                            +{amount} {resource}
                          </span>
                        ))}
                      </div>
                    </div>

                    <button
                      className="launch-button"
                      onClick={() => startMission(mission)}
                      disabled={
                        activeMissions.some((m) => m.id === mission.id) ||
                        !Object.entries(mission.requirements).every(
                          ([resource, amount]) =>
                            (gameState.resources[resource] || gameState.user[resource] || 0) >= amount,
                        )
                      }
                    >
                      <span className="button-icon">🚀</span>
                      Launch Mission
                    </button>
                  </>
                ) : (
                  <div className="unlock-requirement">
                    <span className="lock-icon">🔒</span>
                    <span>Unlock at Level {mission.unlockLevel || 5}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Log */}
      <div className="mission-log">
        <h3 className="panel-title">
          <span className="panel-icon">📜</span>
          Mission Log
        </h3>
        <div className="log-list">
          {missionLog.map((entry) => (
            <div key={entry.id} className={`log-entry ${entry.success ? "success" : "failed"}`}>
              <div className="log-info">
                <span className="log-status">{entry.success ? "✅" : "❌"}</span>
                <div className="log-details">
                  <span className="log-mission">{entry.name}</span>
                  <span className="log-time">{new Date(entry.completed).toLocaleString()}</span>
                </div>
              </div>
              {entry.success && Object.keys(entry.rewards).length > 0 && (
                <div className="log-rewards">
                  {Object.entries(entry.rewards).map(([resource, amount]) => (
                    <span key={resource} className="log-reward">
                      +{amount} {resource}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MissionControl

